/**
 * Image ads from a reference (Oct 29 2026, from Zeely's "Image ad" builder):
 * the AI video ad generator's third format. The user's product photo is
 * reworked into a finished static ad in the layout of a reference ad: one
 * image per picked reference (2 to 10 at once), or a few variations of one.
 *
 * References come from Solmi's own gallery (imageAdReferences: designs we
 * generated for fictional brands, never third-party ads), filtered by
 * concept and industry, or from an upload the user confirms they have the
 * rights to. The words on the ad are written by the server before the image
 * is made (server/imageAdCopyWriter.ts) under Write the line's claim rules,
 * and the image prompt asks for exactly those words and nothing else.
 *
 * Each image is its own creative tool job (tool id "image-ad"), charged at
 * the image editor's price for its engine (Standard: Seedream 5.0 Lite edit,
 * 9 credits; Best: Nano Banana Pro edit, 41 credits), made by the creative
 * job loop through buildCreativeRequest, and listed and downloaded like any
 * other result. Kill switch: CREATIVE_IMAGE_ADS=off.
 */
import type { CreativeTool } from "./creativeTools";
import referenceMedia from "./creativeImageAdReferenceMedia.json";

export const IMAGE_AD_TOOL_ID = "image-ad";

/** The two tiers, on the image editor's own engines and prices. */
export const IMAGE_AD_TIERS = [
  { id: "standard", model: "seedream", credits: 9 },
  { id: "best", model: "rf-edit-nano-banana-pro", credits: 41 },
] as const;
export type ImageAdTier = (typeof IMAGE_AD_TIERS)[number]["id"];
export const imageAdTier = (id: string | undefined) =>
  IMAGE_AD_TIERS.find((tier) => tier.id === id) ?? IMAGE_AD_TIERS[0];
export const imageAdTierForModel = (model: string) =>
  IMAGE_AD_TIERS.find((tier) => tier.model === model);

/** The shapes an image ad comes in: Stories/Reels, feed, square, landscape. */
export const IMAGE_AD_SHAPES = ["9:16", "4:5", "1:1", "16:9"] as const;
export type ImageAdShape = (typeof IMAGE_AD_SHAPES)[number];
export const isImageAdShape = (value: string): value is ImageAdShape =>
  (IMAGE_AD_SHAPES as readonly string[]).includes(value);
/** Seedream's output size per shape (width*height, about 4 megapixels). */
export const IMAGE_AD_SEEDREAM_SIZES: Record<ImageAdShape, string> = {
  "9:16": "1440*2560",
  "4:5": "1728*2160",
  "1:1": "2048*2048",
  "16:9": "2560*1440",
};

/** One Generate makes one image per picked reference, up to this many. */
export const IMAGE_AD_MAX_REFERENCES = 10;
/** Or this many variations of a single reference. */
export const IMAGE_AD_MAX_VARIATIONS = 4;
/**
 * Image ads a user can have in the making at once. They have a limit of
 * their own (a batch of ten would never fit the three shared slots) and
 * don't count toward the other tools' three (server/creativeToolSlots.ts).
 */
export const IMAGE_AD_MAX_ACTIVE = 10;
/** The user's idea for the ad (optional), as the composer's box takes it. */
export const IMAGE_AD_IDEA_MAX = 600;

export const IMAGE_AD_CONCEPTS = [
  "headline",
  "offer",
  "testimonial",
  "before-after",
  "feature-callouts",
  "listicle",
  "split-screen",
  "how-to",
  "ui-style",
] as const;
export type ImageAdConcept = (typeof IMAGE_AD_CONCEPTS)[number];
export const isImageAdConcept = (value: string): value is ImageAdConcept =>
  (IMAGE_AD_CONCEPTS as readonly string[]).includes(value);

export const IMAGE_AD_INDUSTRIES = ["beauty", "fashion", "food", "tech", "home", "fitness", "music"] as const;
export type ImageAdIndustry = (typeof IMAGE_AD_INDUSTRIES)[number];

/**
 * A reference in the gallery: an ad we designed for a fictional brand, and
 * its layout in words (no wording of its own), which the image prompt
 * repeats so the engine takes the layout and not the reference's product or
 * text. Media URLs live in creativeImageAdReferenceMedia.json, written by
 * scripts/creative-image-ad-refs/render.mts.
 */
export type ImageAdReferenceDefinition = {
  id: string;
  concept: ImageAdConcept;
  industry: ImageAdIndustry;
  shape: ImageAdShape;
  layout: string;
};

export const imageAdReferenceDefinitions: ImageAdReferenceDefinition[] = [
  {
    id: "headline-skincare",
    concept: "headline",
    industry: "beauty",
    shape: "4:5",
    layout:
      "a huge bold serif headline across the top third with a small sans-serif line under it, the product standing alone in the lower center on a soft pastel backdrop with a gentle shadow, and a rounded pill button near the bottom",
  },
  {
    id: "headline-vinyl",
    concept: "headline",
    industry: "music",
    shape: "1:1",
    layout:
      "a bold condensed all-caps headline stacked on the left half with a small line under it, the product large on the right half running off the edge, on a dark moody background with one bright accent color",
  },
  {
    id: "offer-sneakers",
    concept: "offer",
    industry: "fashion",
    shape: "9:16",
    layout:
      "the product large in the middle at a dynamic angle on a bright solid backdrop, a big round badge overlapping its upper right, a bold headline at the top and a wide button at the bottom",
  },
  {
    id: "offer-coffee",
    concept: "offer",
    industry: "food",
    shape: "1:1",
    layout:
      "a diagonal ribbon banner across the top left corner, the product centered on a warm textured surface with a few natural props around it, a short headline at the bottom left and a small button at the bottom right",
  },
  {
    id: "testimonial-candle",
    concept: "testimonial",
    industry: "home",
    shape: "4:5",
    layout:
      "a large quote in elegant serif type in the upper half with oversized quotation marks and a short attribution line under it, the product in a cozy lifestyle scene in the lower half",
  },
  {
    id: "testimonial-shaker",
    concept: "testimonial",
    industry: "fitness",
    shape: "9:16",
    layout:
      "a rounded review card floating over a full-bleed photo of the product in use, the quote and a short attribution on the card, and a short headline at the top",
  },
  {
    id: "before-after-cleaner",
    concept: "before-after",
    industry: "home",
    shape: "4:5",
    layout:
      "the frame split down the middle, the left half the situation without the product and the right half the same scene with it, a label at the top of each half, the product standing at the bottom center over the split line, and a headline across the top",
  },
  {
    id: "before-after-steamer",
    concept: "before-after",
    industry: "fashion",
    shape: "9:16",
    layout:
      "two stacked halves, the top one without the product and the bottom one with it, a label in the corner of each half, a thin dividing line with the product resting on it at the right edge, and a small headline at the very top",
  },
  {
    id: "features-earbuds",
    concept: "feature-callouts",
    industry: "tech",
    shape: "1:1",
    layout:
      "the product large in the center on a clean gradient backdrop, three or four short labels around it each joined to the product by a thin pointer line, and a headline at the top",
  },
  {
    id: "features-granola",
    concept: "feature-callouts",
    industry: "food",
    shape: "4:5",
    layout:
      "the product at an angle on the right, a column of three short lines with small round icons on the left, a bold headline at the top left, on a soft solid background",
  },
  {
    id: "listicle-socks",
    concept: "listicle",
    industry: "fitness",
    shape: "4:5",
    layout:
      "a bold headline at the top, a numbered list of short lines down the left side, the product photographed on the right side, on a plain bright background",
  },
  {
    id: "listicle-tour",
    concept: "listicle",
    industry: "music",
    shape: "9:16",
    layout:
      "a tall poster with the headline at the top, three numbered lines in large type down the middle, a photo filling the lower third, and a button at the very bottom",
  },
  {
    id: "split-jacket",
    concept: "split-screen",
    industry: "fashion",
    shape: "9:16",
    layout:
      "two side-by-side halves showing the same product styled two ways, a short label on each half, and a slim headline bar across the top",
  },
  {
    id: "split-lamp",
    concept: "split-screen",
    industry: "home",
    shape: "1:1",
    layout:
      "the left half dark and moody and the right half bright and airy, the product straddling the middle line, a short label at the top of each half",
  },
  {
    id: "howto-mask",
    concept: "how-to",
    industry: "beauty",
    shape: "4:5",
    layout:
      "a headline at the top, then three numbered steps in a row, each with a small photo of that step and a two-word caption, and the product shown large at the bottom",
  },
  {
    id: "howto-coldbrew",
    concept: "how-to",
    industry: "food",
    shape: "9:16",
    layout:
      "a vertical sequence of three numbered panels from top to bottom showing the steps, a short caption beside each number, the product in the last panel, and a headline above the first panel",
  },
  {
    id: "ui-notification",
    concept: "ui-style",
    industry: "tech",
    shape: "9:16",
    layout:
      "a phone lock screen look: a softly blurred photo of the product as the wallpaper, a large clock at the top, and one notification card in the middle holding the app name and the message",
  },
  {
    id: "ui-player",
    concept: "ui-style",
    industry: "music",
    shape: "9:16",
    layout:
      "a music player screen: square cover art in the upper half, the title and a second line under it, a progress bar and play controls, on a softly blurred background taken from the cover's colors",
  },
];

/** A gallery reference with its stored image and thumbnail. */
export type ImageAdReference = ImageAdReferenceDefinition & { image: string; thumb: string };
const media = referenceMedia.references as Record<string, { image: string; thumb: string }>;
/** The gallery, in definition order: only references whose media exists. */
export const imageAdReferences: ImageAdReference[] = imageAdReferenceDefinitions.flatMap((ref) =>
  media[ref.id] ? [{ ...ref, ...media[ref.id] }] : [],
);
export const findImageAdReference = (id: string | undefined) =>
  imageAdReferences.find((ref) => ref.id === id);
/** The gallery reference an image URL is, if it is one. */
export const findImageAdReferenceByUrl = (url: string | undefined) =>
  imageAdReferences.find((ref) => ref.image === url);

/**
 * The words on an image ad, as the copy writer returns them. Every concept
 * has a headline; the rest is per concept (imageAdCopySlots).
 */
export type ImageAdCopy = {
  headline: string;
  subline?: string;
  /** The button. */
  cta?: string;
  /** An offer badge or ribbon: only an offer the product text states. */
  badge?: string;
  /** Listicle lines, feature labels or how-to steps, in order. */
  items?: string[];
  /** Before/after or split-screen labels: [first half, second half]. */
  labels?: [string, string];
  /** A testimonial's quote: the user's own words, never an invented review. */
  quote?: string;
  attribution?: string;
};

/** Longest each part of the copy may be, in characters. */
export const IMAGE_AD_COPY_MAX = {
  headline: 48,
  subline: 70,
  cta: 22,
  badge: 26,
  item: 34,
  label: 18,
  quote: 110,
  attribution: 32,
} as const;

/** What each concept's copy holds (besides the headline), and how many items. */
export const imageAdCopySlots: Record<
  ImageAdConcept,
  { subline?: boolean; cta?: boolean; badge?: boolean; items?: [number, number]; labels?: boolean; quote?: boolean }
> = {
  headline: { subline: true, cta: true },
  offer: { badge: true, cta: true },
  testimonial: { quote: true },
  "before-after": { labels: true },
  "feature-callouts": { items: [3, 4] },
  listicle: { items: [3, 5] },
  "split-screen": { labels: true },
  "how-to": { items: [3, 3] },
  "ui-style": { subline: true, cta: true },
};

/** The copy as the prompt and the page show it: one part a line. */
export function imageAdCopyLines(copy: ImageAdCopy): string[] {
  return [
    `Headline: "${copy.headline}"`,
    ...(copy.subline ? [`Small line: "${copy.subline}"`] : []),
    ...(copy.badge ? [`Badge: "${copy.badge}"`] : []),
    ...(copy.labels ? [`Labels: "${copy.labels[0]}" and "${copy.labels[1]}"`] : []),
    ...(copy.items?.length ? copy.items.map((item, i) => `${i + 1}. "${item}"`) : []),
    ...(copy.quote ? [`Quote: "${copy.quote}"`] : []),
    ...(copy.attribution ? [`Under the quote: "${copy.attribution}"`] : []),
    ...(copy.cta ? [`Button: "${copy.cta}"`] : []),
  ];
}

/** What a concept's drawing must keep to, beyond the words. */
const CONCEPT_RULES: Partial<Record<ImageAdConcept, string>> = {
  testimonial:
    "Draw no stars, ratings, avatars of real-looking customers or review counts; the quote is set as a design element.",
  "before-after":
    "Show the difference only as a change of situation (a messy scene and a tidy one, the product unused and in use); never a body, skin, hair, health or weight result.",
  "split-screen": "Both halves show this product; never another brand's product.",
  "ui-style": "Invent no app names, usernames, prices or numbers; the interface holds only the words given.",
};

/**
 * The image prompt. The images go in as [product, reference, logo?]: the
 * engine keeps the product as it is and takes only the reference's layout,
 * and the words are the copy, exactly, and nothing else (so a reference's
 * own wording, numbers or offers never reach the user's ad).
 */
export function imageAdPrompt({
  concept,
  layout,
  copy,
  shape,
  idea = "",
  buttonColor,
  headlineColor,
  hasLogo,
}: {
  concept: ImageAdConcept;
  /** The gallery reference's layout in words; absent for an uploaded reference. */
  layout?: string;
  copy: ImageAdCopy;
  shape: ImageAdShape;
  idea?: string;
  buttonColor?: string;
  headlineColor?: string;
  hasLogo: boolean;
}): string {
  const orientation =
    shape === "16:9" ? "landscape" : shape === "1:1" ? "square" : shape === "4:5" ? "vertical 4:5" : "tall vertical 9:16";
  const rules = CONCEPT_RULES[concept];
  return [
    `Make a finished, professional ${orientation} social media image ad for the product in image 1.`,
    "Image 1 is the product: keep it exactly as it is, with the same shape, colors, materials, label and printed text, sharp and fully in view.",
    `Image 2 is a reference ad: take only its layout, composition, type style, color mood and graphic elements (${layout ?? "where the text, the product and any badges, panels or lines sit"}). Do not copy its product, brand name, logo, people, numbers or any of its words.`,
    ...(hasLogo
      ? ["Image 3 is the brand's logo: place it small and clean in a corner, unchanged."]
      : ["Add no logo."]),
    `Fit the layout to the ${orientation} frame with safe margins, so no text touches an edge.`,
    "Put exactly this text on the ad, spelled exactly as written, and no other words, letters, numbers, prices, percentages, ratings or hashtags:",
    ...imageAdCopyLines(copy),
    ...(headlineColor ? [`Set the headline in ${headlineColor}.`] : []),
    ...(buttonColor && copy.cta ? [`Make the button ${buttonColor}.`] : []),
    ...(rules ? [rules] : []),
    ...(idea.trim() ? [`The advertiser's idea for the ad (for the scene and mood only): ${idea.trim()}`] : []),
    "Crisp, legible typography and high-end ad design. No watermark.",
  ].join("\n");
}

/**
 * The job's tool entry, so an image ad lists, downloads and refunds like any
 * other creative result (findCreativeToolForJob). Not in creativeTools: it
 * has no page of its own (it is a format of the video ad generator), and the
 * generic create refuses it.
 */
export const imageAdTool: CreativeTool = {
  id: IMAGE_AD_TOOL_ID,
  name: "Image ad",
  description: "A static image ad of the product in the layout of a reference ad.",
  category: "image",
  // The product photo, then the reference ad.
  fields: ["image", "reference"],
  models: IMAGE_AD_TIERS.map((tier) => tier.model),
  // Named on the results like the image editor's tiers.
  tiers: [
    { id: "standard", label: "Standard", model: "seedream", description: "Faithful edits, keeps the product" },
    { id: "best", label: "Best", model: "rf-edit-nano-banana-pro", description: "Sharpest text and detail" },
  ],
};

/**
 * The server's answer when an uploaded reference arrives without the rights
 * line ticked. The page shows the line itself (translated, creativeImageAd).
 */
export const IMAGE_AD_REFERENCE_RIGHTS_REQUIRED =
  "Confirm that you made this reference ad or have the right to use it before it is reworked.";

/**
 * The few words an ad needs when the copy writer can't run (the LLM is down
 * or its copy broke the rules): the button and the before/after labels, in
 * each language the site speaks. Everything else in a fallback copy is the
 * user's own words (server/imageAdCopyWriter.ts).
 */
export const IMAGE_AD_FALLBACK_WORDS: Record<string, { cta: string; before: string; after: string }> = {
  en: { cta: "Shop now", before: "Before", after: "After" },
  es: { cta: "Comprar ahora", before: "Antes", after: "Después" },
  ja: { cta: "今すぐ購入", before: "使用前", after: "使用後" },
  pt: { cta: "Compre agora", before: "Antes", after: "Depois" },
  fr: { cta: "Acheter", before: "Avant", after: "Après" },
  zh: { cta: "立即购买", before: "使用前", after: "使用后" },
  ko: { cta: "지금 구매", before: "사용 전", after: "사용 후" },
  id: { cta: "Beli sekarang", before: "Sebelum", after: "Sesudah" },
  it: { cta: "Acquista ora", before: "Prima", after: "Dopo" },
  nl: { cta: "Nu kopen", before: "Voor", after: "Na" },
  pl: { cta: "Kup teraz", before: "Przed", after: "Po" },
  tr: { cta: "Hemen al", before: "Önce", after: "Sonra" },
  vi: { cta: "Mua ngay", before: "Trước", after: "Sau" },
  de: { cta: "Jetzt kaufen", before: "Vorher", after: "Nachher" },
};
export const imageAdFallbackWords = (language: string | undefined) =>
  IMAGE_AD_FALLBACK_WORDS[(language ?? "").toLowerCase().split("-")[0]] ?? IMAGE_AD_FALLBACK_WORDS.en;
